<template>
  <div class="w-full">
    <div class="w-full flex gap-4 items-center mb-6">
      <div class="flex flex-col items-start text-left min-w-0">
        <div class="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50 leading-snug tracking-tight">
          欢迎回来，<span class="text-primary">词呗AI</span>
        </div>
        <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">未注册用户，登录即注册</div>
      </div>
    </div>

    <div class="w-full mb-4 overflow-hidden rounded-lg bg-white dark:border-white/[0.12] dark:bg-slate-900">
      <div class="flex items-end border-b border-gray-100" role="tablist">
        <button
          v-for="item in visibleLoginTabs"
          :key="item.key"
          type="button"
          role="tab"
          class="relative flex-1 min-w-0 m-0 cursor-pointer select-none outline-none rounded-none"
          :class="activeKey === item.key
            ? 'z-[2] py-2 px-1 text-sm text-primary bg-white dark:bg-slate-900 border border-solid border-[#e8e8e8] dark:border-white/[0.12] border-b-white dark:border-b-slate-900 rounded-t-lg -mb-px'
            : 'p-2 text-sm text-black/88 dark:text-white/65 bg-transparent border-none'"
          :aria-selected="activeKey === item.key"
          @click="handleLoginTypeChange(item.key)"
        >
          {{ item.name }}
        </button>
      </div>

      <div class="relative z-[1] bg-white dark:bg-slate-900 border border-solid border-gray-100 rounded-b-md">
        <QrcodeLoginPanel
          v-if="safeLoginType === LOGIN_TYPE.QRCODE"
          :status="qrcodeStatus"
          :img-url="img_url"
          @refresh="refreshQrcode"
        />
        <OAuthLoginPanel
          v-else-if="safeLoginType === LOGIN_TYPE.OAUTH"
          :loading="oauthLoading"
          :avatar-src="oauthAvatarSrc"
          @authorize="handleOAuthLogin"
        />
        <SmsLoginPanel
          v-else-if="safeLoginType === LOGIN_TYPE.SMS"
          ref="phoneFormRef"
          :form="phoneForm"
          :rules="phoneRules"
          :loading="loading"
          :code-countdown="codeCountdown"
          @submit="handleSubmitLogin"
          @send-code="sendVerificationCode"
        />
        <PasswordLoginPanel
          v-else-if="safeLoginType === LOGIN_TYPE.PASSWORD"
          ref="passwordFormRef"
          :form="passwordForm"
          :rules="passwordRules"
          :loading="loading"
          @submit="handleSubmitLogin"
          @forget-password="handleForgetPassword"
        />
      </div>
    </div>

    <div class="text-sm text-slate-500 dark:text-white/50 flex items-center justify-center leading-6 pb-4">
      <div class="flex pl-1.5">
        登录即表示同意
        <span
          @click.stop="onJumpPage('member_agreement')"
          class="text-slate-700 dark:text-white/80 cursor-pointer px-1.5 hover:text-slate-900 dark:hover:text-white"
        >
          用户协议
        </span>
        和
        <span
          @click.stop="onJumpPage('member_privacy')"
          class="text-slate-700 dark:text-white/80 cursor-pointer px-1.5 hover:text-slate-900 dark:hover:text-white"
        >
          隐私协议
        </span>
      </div>
    </div>

    <custom-modal
      v-model:visible="agreementModal.visible"
      width="800px"
      :header-title="agreementModal.title"
      :footer="null"
    >
      <div v-if="agreementModal.loading" class="flex justify-center py-10">
        <a-spin size="large" />
      </div>
      <div
        v-else
        class="agreement-content overflow-y-auto p-4 leading-7 text-slate-800 dark:text-slate-100 text-sm"
        v-html="agreementModal.content || '暂无'"
      />
    </custom-modal>

    <captcha-dialog
      v-model:visible="captchaDialogVisible"
      @confirm="handleCaptchaConfirm"
      @close="handleCaptchaDialogClose"
    />

    <reset-password ref="resetPasswordRef" @success="handleResetPasswordSuccess" />
  </div>
</template>

<script>
import { loginApi } from '@/api'
import { message } from 'ant-design-vue'
import CustomModal from '@/components/Modal'
import CaptchaDialog from '@/components/CaptchaDialog'
import ResetPassword from '@/components/ResetPassword'
import QrcodeLoginPanel from './components/QrcodeLoginPanel.vue'
import OAuthLoginPanel from './components/OAuthLoginPanel.vue'
import SmsLoginPanel from './components/SmsLoginPanel.vue'
import PasswordLoginPanel from './components/PasswordLoginPanel.vue'
import { LOGIN_TYPE, SMS_COUNTDOWN_SECONDS } from './constants'
import { resolveAuthPayload } from './authResponse'
import {
  applyCredentialAuth,
  finishLoginAfterAuth,
  completeSocialOrSmsLogin
} from './authSession'
import {
  isWechatBrowser,
  normalizeLoginType,
  resolveDefaultLoginType,
  buildVisibleLoginTabs
} from './loginType'
import { createPasswordFormRules, createSmsFormRules } from './formRules'
import { QrcodeLoginController } from './qrcodeLoginController'
import { startOAuthLogin, handleOAuthCallback, shouldHandleOAuthCallback } from './oauthFlow'
import { openAgreementModal } from './agreement'
import { getRegcode } from '@/utils/regcode'
import { validatePhoneForSms } from '@/utils/phoneValidate.js'

export default {
  name: 'UserLogin',
  components: {
    CustomModal,
    CaptchaDialog,
    ResetPassword,
    QrcodeLoginPanel,
    OAuthLoginPanel,
    SmsLoginPanel,
    PasswordLoginPanel
  },
  data () {
    return {
      LOGIN_TYPE,
      oauthAvatarSrc: require('@/assets/img/icon_44.png'),
      redirect: undefined,
      activeKey: LOGIN_TYPE.QRCODE,
      loading: false,
      passwordForm: {
        mobile: '',
        loginpass: ''
      },
      phoneForm: {
        mobile: '',
        code: '',
        regcode: ''
      },
      passwordRules: createPasswordFormRules(),
      phoneRules: createSmsFormRules(),
      codeCountdown: 0,
      countdownTimer: null,
      captchaDialogVisible: false,
      pendingSmsAction: null,
      agreementModal: {
        visible: false,
        title: '',
        content: '',
        loading: false
      },
      qrcodeStatus: 'waiting',
      img_url: '',
      pendingAfterLoginFlow: null,
      oauthLoading: false,
      qrcodeController: null
    }
  },
  watch: {
    $route: {
      handler (route) {
        this.redirect = route.query && route.query.redirect
      },
      immediate: true
    }
  },
  computed: {
    safeLoginType () {
      return normalizeLoginType(this.activeKey)
    },
    visibleLoginTabs () {
      return buildVisibleLoginTabs(this.isWechatBrowser)
    },
    isWechatBrowser () {
      return isWechatBrowser()
    }
  },
  created () {
    this.activeKey = resolveDefaultLoginType({
      loginType: this.$route.query?.loginType
    })

    const regcode = getRegcode(this.$route)
    if (regcode) {
      this.phoneForm.regcode = regcode
    }

    this.qrcodeController = new QrcodeLoginController({
      getRegcode: () => this.phoneForm.regcode,
      onStateChange: (state) => {
        this.qrcodeStatus = state.qrcodeStatus
        this.img_url = state.img_url
      },
      onLoginSuccess: (res, member) => this.onWechatStyleLoginSuccess(res, member),
      store: this.$store
    })

    if (shouldHandleOAuthCallback(this.$route)) {
      this.runOAuthCallback()
      return
    }
    if (this.safeLoginType === LOGIN_TYPE.QRCODE) {
      this.refreshQrcode()
    }
  },
  beforeUnmount () {
    this.clearSmsCountdown()
    this.qrcodeController?.dispose()
    this.qrcodeController = null
  },
  methods: {
    getLoginContext () {
      return {
        store: this.$store,
        router: this.$router,
        route: this.$route,
        redirect: this.redirect
      }
    },

    buildFirstPasswordPromptHandler (res) {
      return (mobile) => {
        this.pendingAfterLoginFlow = async () => {
          await finishLoginAfterAuth({ ...this.getLoginContext(), res })
        }
        this.$refs.resetPasswordRef?.init({
          mode: 'first',
          fromLogin: true,
          mobile
        })
      }
    },

    async onWechatStyleLoginSuccess (res, member) {
      await completeSocialOrSmsLogin({
        ...this.getLoginContext(),
        res,
        member,
        mobileFallback: member?.mobile,
        onPromptSetPassword: this.buildFirstPasswordPromptHandler(res)
      })
    },

    handleLoginTypeChange (type) {
      const nextType = normalizeLoginType(type)

      if (nextType !== LOGIN_TYPE.QRCODE) {
        this.qrcodeController?.reset()
      }

      this.activeKey = nextType

      if (nextType === LOGIN_TYPE.QRCODE) {
        this.refreshQrcode()
      }

      this.$refs.passwordFormRef?.clearValidate()
      this.$refs.phoneFormRef?.clearValidate()
    },

    refreshQrcode () {
      this.qrcodeController?.refresh()
    },

    clearSmsCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    startSmsCountdown () {
      this.clearSmsCountdown()
      this.codeCountdown = SMS_COUNTDOWN_SECONDS
      this.countdownTimer = setInterval(() => {
        this.codeCountdown--
        if (this.codeCountdown <= 0) {
          this.clearSmsCountdown()
        }
      }, 1000)
    },

    async sendVerificationCode () {
      const pv = validatePhoneForSms(this.phoneForm.mobile)
      if (!pv.ok) {
        message.error(pv.message)
        return
      }
      if (this.codeCountdown > 0) return

      this.pendingSmsAction = { mobile: pv.mobile, type: 'login' }
      this.captchaDialogVisible = true
    },

    async handleCaptchaConfirm ({ captcha, uuid }) {
      if (!this.pendingSmsAction) {
        message.error('操作异常，请重试')
        this.captchaDialogVisible = false
        return
      }
      try {
        await loginApi.sendSms({
          mobile: this.pendingSmsAction.mobile,
          type: this.pendingSmsAction.type,
          captcha,
          uuid
        })
        this.captchaDialogVisible = false
        this.pendingSmsAction = null
        this.startSmsCountdown()
        message.success('验证码已发送，请注意查收')
      } catch (err) {
        console.error('发送验证码失败', err)
        message.error(err?.msg || err?.message || '发送验证码失败')
      }
    },

    handleCaptchaDialogClose () {
      this.captchaDialogVisible = false
      this.pendingSmsAction = null
    },

    async handlePasswordLogin () {
      try {
        await this.$refs.passwordFormRef.validate()
        this.loading = true
        const res = await loginApi.loginByPassword({
          mobile: this.passwordForm.mobile,
          loginpass: this.passwordForm.loginpass
        })
        const responseData = resolveAuthPayload(res)
        applyCredentialAuth(this.$store, responseData)
        await finishLoginAfterAuth({ ...this.getLoginContext(), res })
      } catch (err) {
        if (err?.errorFields) return
        console.error('登录失败', err)
        message.error(err?.msg || err?.message || '登录失败')
      } finally {
        this.loading = false
      }
    },

    async handlePhoneLogin () {
      try {
        await this.$refs.phoneFormRef.validate()
        this.loading = true
        const pv = validatePhoneForSms(this.phoneForm.mobile)
        if (!pv.ok) {
          message.error(pv.message)
          return
        }
        const res = await loginApi.codeLoginByPassword({
          mobile: pv.mobile,
          code: this.phoneForm.code,
          regcode: this.phoneForm.regcode
        })
        const responseData = resolveAuthPayload(res)
        const member = applyCredentialAuth(this.$store, responseData)
        await completeSocialOrSmsLogin({
          ...this.getLoginContext(),
          res,
          member: member || responseData?.member || responseData?.userInfo || {},
          mobileFallback: this.phoneForm.mobile,
          onPromptSetPassword: this.buildFirstPasswordPromptHandler(res)
        })
      } catch (err) {
        if (err?.errorFields) return
        console.error('验证码登录失败', err)
        message.error(err?.msg || err?.message || '登录失败')
      } finally {
        this.loading = false
      }
    },

    handleSubmitLogin () {
      if (this.safeLoginType === LOGIN_TYPE.PASSWORD) {
        this.handlePasswordLogin()
      } else {
        this.handlePhoneLogin()
      }
    },

    handleForgetPassword () {
      this.$refs.resetPasswordRef?.init({
        mode: 'reset',
        fromLogin: true,
        mobile: this.passwordForm.mobile || ''
      })
    },

    handleResetPasswordSuccess () {
      if (typeof this.pendingAfterLoginFlow === 'function') {
        const pendingFlow = this.pendingAfterLoginFlow
        this.pendingAfterLoginFlow = null
        pendingFlow()
        return
      }
      const mobile = this.$refs.resetPasswordRef?.form?.mobile
      if (mobile) {
        this.passwordForm.mobile = mobile
      }
      this.passwordForm.loginpass = ''
      this.handleLoginTypeChange(LOGIN_TYPE.PASSWORD)
    },

    async handleOAuthLogin () {
      this.oauthLoading = true
      const started = await startOAuthLogin()
      if (!started) {
        this.oauthLoading = false
      }
    },

    async runOAuthCallback () {
      this.oauthLoading = true
      const ok = await handleOAuthCallback({
        route: this.$route,
        store: this.$store,
        onLoginSuccess: (res, member) => this.onWechatStyleLoginSuccess(res, member)
      })
      if (!ok) {
        this.oauthLoading = false
      }
    },

    onJumpPage (type) {
      return openAgreementModal(this.agreementModal, type)
    }
  }
}
</script>

<style scoped>
.agreement-content :deep(h1),
.agreement-content :deep(h2),
.agreement-content :deep(h3),
.agreement-content :deep(h4),
.agreement-content :deep(h5),
.agreement-content :deep(h6) {
  @apply my-4 font-bold bg-transparent;
}

.agreement-content :deep(p) {
  @apply my-3 bg-transparent;
}

.agreement-content :deep(ul),
.agreement-content :deep(ol) {
  @apply my-3 pl-6 bg-transparent;
}

.agreement-content :deep(li) {
  @apply my-1.5 bg-transparent;
}

.agreement-content :deep(img) {
  @apply max-w-full h-auto rounded bg-transparent;
}
</style>
